import React, { useState, useRef, useEffect } from "react";

interface OptimizedImageProps {
  src: string;
  thumbnailSrc?: string;
  alt: string;
  className?: string;
  onError?: (e: React.SyntheticEvent<HTMLImageElement, Event>) => void;
  loading?: "lazy" | "eager";
}

const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  thumbnailSrc,
  alt,
  className = "",
  onError,
  loading = "lazy",
}) => {
  const [currentSrc, setCurrentSrc] = useState(thumbnailSrc || src);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(loading === "eager");
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    if (loading === "eager") {
      setIsInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, [loading]);

  useEffect(() => {
    if (isInView && src && thumbnailSrc && currentSrc === thumbnailSrc) {
      // Load the full resolution image
      const img = new Image();
      img.onload = () => {
        setCurrentSrc(src);
        setIsLoaded(true);
      };
      img.onerror = () => {
        console.error("Failed to load full image:", src);
        // Keep showing thumbnail if full image fails
      };
      img.src = src;
    } else if (isInView && src && !thumbnailSrc) {
      // No thumbnail, just load the main image
      setIsLoaded(true);
    }
  }, [isInView, src, thumbnailSrc, currentSrc]);

  const handleError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    if (onError) {
      onError(e);
    }
  };

  return (
    <div className="relative w-full h-full">
      <img
        ref={imgRef}
        src={currentSrc}
        alt={alt}
        className={`${className} ${
          !isLoaded && thumbnailSrc ? "blur-sm" : ""
        } transition-all duration-300`}
        onError={handleError}
        loading={loading}
      />
      {!currentSrc && (
        <div className="absolute inset-0 bg-gray-200 animate-pulse rounded" />
      )}
    </div>
  );
};

export default OptimizedImage;
